// Policy content drafts for Incer Technovation Pvt Ltd.
// Review and customize the text below before going live.

const COMPANY = "Incer Technovation Pvt Ltd";
const EMAIL = "contact@incer.in";
const ADDRESS = "45/13 Vettukattuvalasu, Vivekanandar Salai, Erode – 638011, Tamil Nadu, India";
const EFFECTIVE = "10 December 2025";

export const PRIVACY = {
  slug: "privacy",
  title: "Privacy Policy",
  intro: `This Privacy Policy explains how ${COMPANY} ("Incer", "we", "us") collects, uses, shares and protects information when you visit our website, sign up as a brand or creator, or otherwise engage with our influencer marketing platform.`,
  effective: EFFECTIVE,
  sections: [
    {
      h: "1. Information we collect",
      p: [
        "Account information you provide when signing up — name, email, phone, company or social media handles, niche and audience details.",
        "Usage data such as pages visited, links clicked, device and browser information, IP address and approximate location.",
        "Campaign data including briefs, communications, deliverables and performance metrics from connected creator accounts (with your consent).",
        "Cookies and similar tracking technologies as described in our Cookie Policy.",
      ],
    },
    {
      h: "2. How we use information",
      p: [
        "To operate, maintain and improve the Incer platform and matchmaking features.",
        "To verify creators, run campaigns, coordinate deliverables and provide analytics to brands.",
        "To communicate with you about your account, campaigns, opportunities, support requests and product updates.",
        "To detect, prevent and address fraud, abuse or security incidents.",
        "To comply with applicable laws and respond to lawful requests from authorities.",
      ],
    },
    {
      h: "3. How we share information",
      p: [
        "With brands and creators participating in a campaign you are part of — strictly for collaboration purposes.",
        "With service providers (hosting, analytics, email, payments) who process data on our behalf under contractual safeguards.",
        "With professional advisors and authorities when legally required.",
        "We do not sell your personal data to third parties.",
      ],
    },
    {
      h: "4. Your choices and rights",
      p: [
        "You can access, update or delete your account information by writing to us at " + EMAIL + ".",
        "You may opt out of marketing emails using the unsubscribe link in each message.",
        "Where applicable, you have rights to object to processing, request portability or restrict use of your data under local data protection laws.",
      ],
    },
    {
      h: "5. Data retention and security",
      p: [
        "We retain personal data for as long as your account is active and as required for legitimate business and legal purposes.",
        "We use reasonable administrative, technical and physical safeguards to protect your data. However, no online service is fully secure and we cannot guarantee absolute security.",
      ],
    },
    {
      h: "6. International transfers",
      p: [
        "Your information may be processed outside your country of residence, including in jurisdictions with different data protection laws. We rely on appropriate safeguards for such transfers.",
      ],
    },
    {
      h: "7. Changes to this policy",
      p: [
        "We may update this Privacy Policy from time to time. Material changes will be highlighted on this page along with a revised effective date.",
      ],
    },
    {
      h: "8. Contact",
      p: [
        `For questions about this policy or your personal data, contact us at ${EMAIL} or write to ${ADDRESS}.`,
      ],
    },
  ],
};

export const TERMS = {
  slug: "terms",
  title: "Terms of Service",
  intro: `These Terms of Service ("Terms") govern your use of the ${COMPANY} website and influencer marketing platform ("Incer", "Services"). By accessing or using the Services you agree to be bound by these Terms.`,
  effective: EFFECTIVE,
  sections: [
    {
      h: "1. Eligibility",
      p: [
        "You must be at least 18 years old and capable of entering into a legally binding contract to use Incer. By using the Services you represent and warrant that you meet these requirements.",
      ],
    },
    {
      h: "2. Accounts",
      p: [
        "You are responsible for the accuracy of information you provide and for keeping your login credentials confidential.",
        "You are responsible for all activity that occurs under your account. Notify us immediately if you suspect unauthorized access.",
      ],
    },
    {
      h: "3. Use of the platform",
      p: [
        "Brands may use Incer to discover creators, plan campaigns, coordinate deliverables and review performance.",
        "Creators may use Incer to receive collaboration opportunities, submit deliverables and grow their professional portfolio.",
        "You may not use the Services to upload unlawful, infringing, misleading, abusive or harmful content, or to interfere with other users or our infrastructure.",
      ],
    },
    {
      h: "4. Campaigns and creator collaborations",
      p: [
        "Each campaign is governed by a specific brief, deliverables and timelines agreed between the brand, the creator and Incer.",
        "Creators are independent contractors. Incer is not the employer of creators and does not guarantee any specific campaign, payment or outcome unless expressly committed in writing.",
        "Brands are responsible for the legality and accuracy of campaign briefs, product claims and brand assets they share.",
      ],
    },
    {
      h: "5. Fees and payment",
      p: [
        "Fees, deliverables and payment timelines for each engagement are agreed separately in a quote, statement of work or campaign agreement.",
        "Unless otherwise stated, all fees are exclusive of applicable taxes which will be added at the prevailing rate.",
      ],
    },
    {
      h: "6. Intellectual property",
      p: [
        "Incer, the Incer logo and the Services (excluding user content) are owned by " + COMPANY + " and protected by intellectual property laws.",
        "You retain ownership of content you submit to the platform but grant Incer a limited license to host, display and use the content as needed to provide the Services.",
        "Usage rights for creator content in brand campaigns are governed by the specific campaign agreement.",
      ],
    },
    {
      h: "7. Termination",
      p: [
        "We may suspend or terminate your access to the Services at any time for breach of these Terms, suspected fraud or risk to other users.",
        "You may close your account by writing to " + EMAIL + ".",
      ],
    },
    {
      h: "8. Disclaimers and limitation of liability",
      p: [
        "The Services are provided on an 'as is' and 'as available' basis without warranties of any kind.",
        "To the maximum extent permitted by law, Incer is not liable for indirect, incidental, special or consequential damages, or for any loss of profits, revenue, data, goodwill or business opportunities arising from your use of the Services.",
      ],
    },
    {
      h: "9. Governing law",
      p: [
        "These Terms are governed by the laws of India. Any disputes arising out of or in connection with these Terms are subject to the exclusive jurisdiction of the courts of Erode, Tamil Nadu.",
      ],
    },
    {
      h: "10. Contact",
      p: [
        `Questions about these Terms can be sent to ${EMAIL}.`,
      ],
    },
  ],
};

export const REFUND = {
  slug: "refund",
  title: "Refund & Cancellation Policy",
  intro: `This Refund & Cancellation Policy explains the conditions under which fees paid to ${COMPANY} for influencer marketing services may be refunded or campaigns cancelled.`,
  effective: EFFECTIVE,
  sections: [
    {
      h: "1. Campaign payments",
      p: [
        "Influencer marketing campaigns typically require an advance payment before execution. The exact split between advance, milestone and final payment is captured in the campaign agreement or invoice.",
      ],
    },
    {
      h: "2. Cancellation by the brand",
      p: [
        "Cancellation before creator outreach has started: eligible for a refund of the advance, less any setup and platform fees already incurred.",
        "Cancellation after creator outreach or sign-off has started but before content is delivered: up to 50% of campaign value is non-refundable to cover work already executed by Incer and creators.",
        "Cancellation after content has been created or delivered: no refund will be issued. Already-incurred creator fees, production costs and platform charges remain payable.",
      ],
    },
    {
      h: "3. Cancellation by Incer",
      p: [
        "If we are unable to deliver a campaign for reasons attributable to Incer, we will refund the unconsumed portion of the advance or substitute creators and deliverables of equivalent value, at the brand's option.",
      ],
    },
    {
      h: "4. Creator non-performance",
      p: [
        "If a confirmed creator fails to deliver content as agreed, Incer will work to assign a replacement creator of similar profile at no additional cost. If a replacement is not feasible, the fee allocated to that creator will be refunded.",
      ],
    },
    {
      h: "5. Refund timelines",
      p: [
        "Approved refunds will be processed to the original payment method within 7–14 working days of approval. Bank or gateway processing times may add additional delay outside our control.",
      ],
    },
    {
      h: "6. Non-refundable items",
      p: [
        "Onboarding fees, platform access fees, content licensing fees, and taxes already paid to authorities are non-refundable.",
        "Custom strategy work, research reports and creative deliverables that have already been shared are non-refundable.",
      ],
    },
    {
      h: "7. How to request a refund",
      p: [
        `Email ${EMAIL} with your campaign reference, invoice number and reason for cancellation. We will review and respond within 5 working days.`,
      ],
    },
  ],
};

export const COOKIES = {
  slug: "cookies",
  title: "Cookie Policy",
  intro: `This Cookie Policy explains how ${COMPANY} uses cookies and similar technologies on the Incer website and platform.`,
  effective: EFFECTIVE,
  sections: [
    {
      h: "1. What are cookies",
      p: [
        "Cookies are small text files placed on your device when you visit a website. They help the site remember your preferences, sign-in state and how you use the site.",
      ],
    },
    {
      h: "2. Types of cookies we use",
      p: [
        "Essential cookies: required for the platform to function — for example, keeping you signed in.",
        "Analytics cookies: help us understand how visitors use the site so we can improve content and performance (e.g. Google Analytics, Plausible).",
        "Functional cookies: remember your preferences such as language and region.",
        "Marketing cookies: used to measure the effectiveness of campaigns and may be set by third-party advertising partners with your consent.",
      ],
    },
    {
      h: "3. Managing cookies",
      p: [
        "Most browsers let you block or delete cookies from the browser settings. Disabling essential cookies may affect parts of the site that require them.",
        "Where required by law, we will request your consent before placing non-essential cookies.",
      ],
    },
    {
      h: "4. Third-party services",
      p: [
        "We may use third-party services that set their own cookies — including analytics providers, embedded videos and social media widgets. Those cookies are governed by the third party's own policy.",
      ],
    },
    {
      h: "5. Contact",
      p: [
        `For questions about cookies on this site, contact ${EMAIL}.`,
      ],
    },
  ],
};

export const DISCLAIMER = {
  slug: "disclaimer",
  title: "Disclaimer",
  intro: `The information provided by ${COMPANY} on the Incer website and platform is for general informational and business purposes only.`,
  effective: EFFECTIVE,
  sections: [
    {
      h: "1. No guarantee of results",
      p: [
        "Influencer marketing outcomes depend on many variables including creative quality, audience response, market conditions and brand fit. While Incer applies best practices and reasonable effort, we do not guarantee specific reach, engagement, conversion or revenue outcomes.",
      ],
    },
    {
      h: "2. Third-party content",
      p: [
        "Creator content posted on third-party platforms (Instagram, YouTube, etc.) is governed by those platforms' own policies. Incer is not responsible for changes to platform algorithms, takedowns or policy actions outside our control.",
      ],
    },
    {
      h: "3. External links",
      p: [
        "Our website may contain links to third-party websites. We do not endorse and are not responsible for the content, accuracy or practices of those websites.",
      ],
    },
    {
      h: "4. Professional advice",
      p: [
        "Content on this site is not legal, financial or tax advice. Consult qualified professionals before making decisions based on information provided here.",
      ],
    },
    {
      h: "5. Trademarks",
      p: [
        "Brand names, logos and trademarks displayed on this site belong to their respective owners and are used for identification or illustration purposes only.",
      ],
    },
    {
      h: "6. Contact",
      p: [
        `Questions about this disclaimer can be sent to ${EMAIL}.`,
      ],
    },
  ],
};

export const ALL_POLICIES = [PRIVACY, TERMS, REFUND, COOKIES, DISCLAIMER];
